import { observer } from 'mobx-react'
import { GetStaticProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { MyHead } from '@/components/molecules/MyHead'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { IUserBase, Language, UserProfileJobHuntingStatus, UserProfileYearsOfExperience } from '@/types'
import { getStaticStores, useStores } from '@/utils'
import { useState, useEffect } from 'react'
import { Button } from '@/components/atoms/Button'
import { ProfileCompletionPromotionPopup } from '@/components/organisms/ProfileCompletionPromotionPopup'
import {
  FetchJobSeekersValues,
  KeywordSearchGroupForJobSeekers,
} from '@/components/organisms/KeywordSearchGroupForJobSeekers'
import { JobSeekerCardList } from '@/components/organisms/JobSeekerCardList'
import styles from './index.module.scss'

type Props = {
  staticJobSeekers: IUserBase[]
  staticHasNexJobSeekersPage: boolean
}

const NUM_OF_ITEMS = 20

const Experts: NextPage<Props> = observer(({ staticJobSeekers, staticHasNexJobSeekersPage }) => {
  const { users } = useStores()

  const [searchResults, setSearchResults] = useState<IUserBase[]>(
    users.jobSeekers.length > 0 ? users.jobSeekers : staticJobSeekers
  )
  const [searchKeyword, setSearchKeyword] = useState('')
  const [jobHuntingStatus, setJobHuntingStatus] = useState<UserProfileJobHuntingStatus[]>([])
  const [primaryJobCategoryIds, setPrimaryJobCategoryIds] = useState<string[]>([])
  const [yearsOfExperience, setYearsOfExperience] = useState<UserProfileYearsOfExperience[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const searchHandler = async (input: FetchJobSeekersValues) => {
    const result = await users.fetchJobSeekers({
      searchWord: input.keyword,
      jobHuntingStatus: input.jobHuntingStatus,
      primaryJobCategoryIds: input.primaryJobCategories,
      yearsOfExperience: input.yearsOfExperience,
      limit: NUM_OF_ITEMS,
      shouldRefresh: true,
    })
    setSearchResults(result)
  }

  const fetchJobSeekers = async () => {
    setIsFetching(true)
    const isFirstTimeFetching = staticJobSeekers === searchResults
    await users.fetchJobSeekers({
      searchWord: searchKeyword,
      jobHuntingStatus,
      primaryJobCategoryIds,
      yearsOfExperience,
      limit: isFirstTimeFetching ? NUM_OF_ITEMS * 2 : NUM_OF_ITEMS, // 初回の追加読み込みは ISR 分も含めて倍読み込む
      shouldRefresh: isFirstTimeFetching, // 初回の追加読み込みは Store の中身をリセットするために shouldRefresh: true
    })
    setSearchResults(users.jobSeekers)
    setIsFetching(false)
  }

  const setSearchValues = async (input: FetchJobSeekersValues) => {
    setSearchKeyword(input.keyword)
    setJobHuntingStatus(input.jobHuntingStatus)
    setPrimaryJobCategoryIds(input.primaryJobCategories)
    setYearsOfExperience(input.yearsOfExperience)
    await searchHandler(input)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    users.updateHasNextJobSeekersPage(staticHasNexJobSeekersPage)
  }, [])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title='Search for Experts' />
      <ProfileSingleColumnContentTemplate>
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='Experts'>Experts</PrimaryHeading>
        </div>
        <KeywordSearchGroupForJobSeekers search={async (input: FetchJobSeekersValues) => setSearchValues(input)} />
        <div className={styles.content}>
          <JobSeekerCardList items={searchResults} />
        </div>
        {mounted && users.hasNextJobSeekersPage && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              // 最後まで読み込んだら disabled にする
              disabled={isFetching}
              onClick={() => fetchJobSeekers()}
            >
              + Load More
            </Button>
          </div>
        )}
        <ProfileCompletionPromotionPopup type='jobSeeker' />
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { viewer, users } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const staticJobSeekers = await users.fetchJobSeekers({ limit: NUM_OF_ITEMS, shouldRefresh: true })

  return {
    props: {
      staticJobSeekers,
      staticHasNexJobSeekersPage: users.hasNextJobSeekersPage,
      ...(await serverSideTranslations(locale, [
        'companies-users-[id]',
        'p',
        'global',
        'angels-vcs-startups',
        'search',
      ])),
    },
    revalidate: 1,
  }
}

export default Experts
